export const initialState = {
  warning: null,
  isIntroModalOpen: true,
  isBusinessPlanModalOpen: false,
  isConclusionsModalOpen: false,
  isForecastsModalOpen: false,
  isDecisionFormsModalOpen: false,
  businessPlanModalStep: 0,
  conclusionsModalStep: 0,
  decisionFormsModalStep: 0,
  year1TutorialStep: null,
  year0DialogueCompleted: false,
  year0CompletedTutorials: {},
  year1CompletedTutorials: {},
  tutorialStateLoaded: false,
  tutorialInteractionCompleted: null // Tracks which tutorial had interaction completed
}

// eslint-disable-next-line default-param-last
export function tutorReducer(state = initialState, action) {
  switch (action.type) {
    case 'tutor/setWarning':
      return { ...state, warning: action.payload }
    case 'tutor/clearWarning':
      return { ...state, warning: null }
    case 'tutor/setIntroModalOpen':
      return { ...state, isIntroModalOpen: action.payload }
    case 'tutor/setBusinessPlanModalOpen':
      return { ...state, isBusinessPlanModalOpen: action.payload }
    case 'tutor/setBusinessPlanModalStep':
      return { ...state, businessPlanModalStep: action.payload }
    case 'tutor/setConclusionsModalOpen':
      return { ...state, isConclusionsModalOpen: action.payload }
    case 'tutor/setConclusionsModalStep':
      return { ...state, conclusionsModalStep: action.payload }
    case 'tutor/setForecastsModalOpen':
      return { ...state, isForecastsModalOpen: action.payload }
    case 'tutor/setDecisionFormsModalOpen':
      return { ...state, isDecisionFormsModalOpen: action.payload }
    case 'tutor/setDecisionFormsModalStep':
      return { ...state, decisionFormsModalStep: action.payload }
    case 'tutor/setYear1TutorialStep':
      return { ...state, year1TutorialStep: action.payload }
    case 'tutor/setYear0DialogueCompleted':
      return { ...state, year0DialogueCompleted: action.payload }
    case 'tutor/setYear0TutorialCompleted':
      return {
        ...state,
        year0CompletedTutorials: {
          ...state.year0CompletedTutorials,
          [action.payload]: true
        }
      }
    case 'tutor/setYear1TutorialCompleted':
      return {
        ...state,
        year1CompletedTutorials: {
          ...state.year1CompletedTutorials,
          [action.payload]: true
        }
      }
    case 'tutor/loadTutorialState':
      return {
        ...state,
        year1TutorialStep: action.payload.year1TutorialStep || null,
        year0DialogueCompleted: action.payload.year0DialogueCompleted || false,
        year0CompletedTutorials: action.payload.year0CompletedTutorials || {},
        year1CompletedTutorials: action.payload.year1CompletedTutorials || {},
        tutorialStateLoaded: true
      }
    case 'tutor/setTutorialInteractionCompleted':
      return { ...state, tutorialInteractionCompleted: action.payload }
    default:
      return state
  }
}

export default tutorReducer

export const setWarning = (
  message,
  type = 'action',
  notificationPosition = 'bottom',
  notificationMargin = null
) => ({
  type: 'tutor/setWarning',
  payload: { message, type, notificationPosition, notificationMargin }
})

export const clearWarning = () => ({
  type: 'tutor/clearWarning'
})

export const setIntroModalOpen = (isOpen) => ({
  type: 'tutor/setIntroModalOpen',
  payload: isOpen
})

export const setBusinessPlanModalOpen = (isOpen) => ({
  type: 'tutor/setBusinessPlanModalOpen',
  payload: isOpen
})

export const setBusinessPlanModalStep = (step) => ({
  type: 'tutor/setBusinessPlanModalStep',
  payload: step
})

export const setConclusionsModalOpen = (isOpen) => ({
  type: 'tutor/setConclusionsModalOpen',
  payload: isOpen
})

export const setConclusionsModalStep = (step) => ({
  type: 'tutor/setConclusionsModalStep',
  payload: step
})

export const setForecastsModalOpen = (isOpen) => ({
  type: 'tutor/setForecastsModalOpen',
  payload: isOpen
})

export const setDecisionFormsModalOpen = (isOpen) => ({
  type: 'tutor/setDecisionFormsModalOpen',
  payload: isOpen
})

export const setDecisionFormsModalStep = (step) => ({
  type: 'tutor/setDecisionFormsModalStep',
  payload: step
})

export const setTutorialInteractionCompleted = (actionState) => ({
  type: 'tutor/setTutorialInteractionCompleted',
  payload: actionState
})

// Helper to get game-specific localStorage key
const getGameStorageKey = (gameId, key) => `game_${gameId}_${key}`

// Load tutorial state from localStorage for a specific game
export const loadTutorialState = () => (dispatch, getState) => {
  const state = getState()
  const gameId = state.user?.game?.data?.id

  if (!gameId) return

  try {
    const year1Step = localStorage.getItem(getGameStorageKey(gameId, 'year1TutorialStep'))
    const year0Completed = localStorage.getItem(getGameStorageKey(gameId, 'year0DialogueCompleted'))
    const year0CompletedTutorialsStr = localStorage.getItem(
      getGameStorageKey(gameId, 'year0CompletedTutorials')
    )
    const year1CompletedTutorialsStr = localStorage.getItem(
      getGameStorageKey(gameId, 'year1CompletedTutorials')
    )

    let year0CompletedTutorials = {}
    if (year0CompletedTutorialsStr) {
      try {
        year0CompletedTutorials = JSON.parse(year0CompletedTutorialsStr)
      } catch {
        // If parsing fails, use empty object
        year0CompletedTutorials = {}
      }
    }

    let year1CompletedTutorials = {}
    if (year1CompletedTutorialsStr) {
      try {
        year1CompletedTutorials = JSON.parse(year1CompletedTutorialsStr)
      } catch {
        // If parsing fails, use empty object
        year1CompletedTutorials = {}
      }
    }

    dispatch({
      type: 'tutor/loadTutorialState',
      payload: {
        year1TutorialStep: year1Step || null,
        year0DialogueCompleted: year0Completed === 'true',
        year0CompletedTutorials,
        year1CompletedTutorials
      }
    })
  } catch (error) {
    // Ignore localStorage errors
  }
}

// Set Year 1 tutorial step with game-specific persistence
export const setYear1TutorialStep = (step) => (dispatch, getState) => {
  const state = getState()
  const gameId = state.user?.game?.data?.id

  // Always dispatch to Redux state
  dispatch({
    type: 'tutor/setYear1TutorialStep',
    payload: step
  })

  // Persist to localStorage with game-specific key
  if (gameId) {
    try {
      if (step === null) {
        localStorage.removeItem(getGameStorageKey(gameId, 'year1TutorialStep'))
      } else {
        localStorage.setItem(getGameStorageKey(gameId, 'year1TutorialStep'), step)
      }
    } catch (error) {
      // Ignore localStorage errors
    }
  }
}

// Set Year 0 dialogue completed with game-specific persistence
export const setYear0DialogueCompleted = (completed) => (dispatch, getState) => {
  const state = getState()
  const gameId = state.user?.game?.data?.id

  // Always dispatch to Redux state
  dispatch({
    type: 'tutor/setYear0DialogueCompleted',
    payload: completed
  })

  // Persist to localStorage with game-specific key
  if (gameId) {
    try {
      localStorage.setItem(
        getGameStorageKey(gameId, 'year0DialogueCompleted'),
        completed.toString()
      )
    } catch (error) {
      // Ignore localStorage errors
    }
  }
}

// Mark a Year 0 RoboTutor tutorial as completed with game-specific persistence
export const setYear0TutorialCompleted = (messageKey) => (dispatch, getState) => {
  const state = getState()
  const gameId = state.user?.game?.data?.id

  // Always dispatch to Redux state
  dispatch({
    type: 'tutor/setYear0TutorialCompleted',
    payload: messageKey
  })

  // Persist to localStorage with game-specific key
  if (gameId) {
    try {
      const currentCompletedStr = localStorage.getItem(
        getGameStorageKey(gameId, 'year0CompletedTutorials')
      )
      let currentCompleted = {}
      if (currentCompletedStr) {
        try {
          currentCompleted = JSON.parse(currentCompletedStr)
        } catch {
          currentCompleted = {}
        }
      }

      // Add the new completed tutorial
      currentCompleted[messageKey] = true

      localStorage.setItem(
        getGameStorageKey(gameId, 'year0CompletedTutorials'),
        JSON.stringify(currentCompleted)
      )
    } catch (error) {
      // Ignore localStorage errors
    }
  }
}

export const setYear1TutorialCompleted = (messageKey) => (dispatch, getState) => {
  const state = getState()
  const gameId = state.user?.game?.data?.id

  // Always dispatch to Redux state
  dispatch({
    type: 'tutor/setYear1TutorialCompleted',
    payload: messageKey
  })

  // Persist to localStorage with game-specific key
  if (gameId) {
    try {
      const currentCompletedStr = localStorage.getItem(
        getGameStorageKey(gameId, 'year1CompletedTutorials')
      )
      let currentCompleted = {}
      if (currentCompletedStr) {
        try {
          currentCompleted = JSON.parse(currentCompletedStr)
        } catch {
          currentCompleted = {}
        }
      }

      // Add the new completed tutorial
      currentCompleted[messageKey] = true

      localStorage.setItem(
        getGameStorageKey(gameId, 'year1CompletedTutorials'),
        JSON.stringify(currentCompleted)
      )
    } catch (error) {
      // Ignore localStorage errors
    }
  }
}
